import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { useTranslation } from "react-i18next";
import { Breakpoint } from "react-socks";
import * as ActionTypes from "../actions/actionTypes";
import { getPromotionItems } from "../actions/promotionActions";
import { getStockItemToAdd } from "../actions/promotionActions";
import { addToCart,removeFromCart} from "../actions/cartActions";
import {buildCheckoutSale,storeCheckoutSale} from "../actions/checkoutActions";
import { isPromoLine, cartLineKey } from "../common/cartUtils";


export default function PromotionList() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const checkoutSale = useSelector((state) => state.checkoutSale);

    // Read needed data from Redux for getSaleData
  const user = useSelector((state) => state.user);
  const cart = useSelector((state) => state.cart);
  const shipping = useSelector((state) => state.shipping);
  const payment = useSelector((state) => state.payment);
  const discountCode = useSelector((state) => state.discountCode);
  const settings = useSelector((state) => state.settings);

  const { items, loading } = useSelector((state) => {
    const list = state.promotions.list;

    if (!list) {
      return { items: [], loading: true };
    }
    return {
      items: list.items || [],
      loading: list.loading || false,
    };
  });
  const [addingItem, setAddingItem] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

const getSaleData = () => {
  // use hooks values directly
  const invalidCartLines = cart.lines
    .filter(line => line.stockItemId === undefined || line.stockItemId === 0)
    .map(line => line.product.title);

  if (invalidCartLines.length > 0) {
    console.error("Invalid cart:", invalidCartLines);
    return null;
  }

  const itemDetails = checkoutSale?.itemDetails ?? [];

  // Remove promo lines not present in checkoutSale
  const filteredCartLines = cart.lines.filter(line => {
    const isPromoAndNotInItemDetails =
      isPromoLine(line.isPromo) &&
      !itemDetails.some(
        item =>
          item.stockItemId === line.stockItemId &&
          isPromoLine(item.isPromotion)
      );

    if (isPromoAndNotInItemDetails) {
      dispatch(removeFromCart({ stockItemId: line.stockItemId, isPromo: line.isPromo }));
      return false;
    }
    return true;
  });

  // Map: stockItemId + isPromo
  const cartMap = new Map(
    filteredCartLines.map(line => [
      cartLineKey(line.stockItemId, line.isPromo),
      line.quantity,
    ])
  );

  const updatedExistingItems = itemDetails
    .map(item => {
      const matchingLines = filteredCartLines.filter(
        line =>
          line.stockItemId === item.stockItemId &&
          isPromoLine(line.isPromo) === isPromoLine(item.isPromotion)
      );

      return matchingLines.map(line => ({
        ...item,
        quantity: cartMap.get(cartLineKey(line.stockItemId, line.isPromo)),
        isPromo: isPromoLine(line.isPromo),
      }));
    })
    .flat();

  const existingItemKeys = new Set(
    itemDetails.map(i => cartLineKey(i.stockItemId, i.isPromotion))
);

const newItems = filteredCartLines
    .filter(line => !existingItemKeys.has(cartLineKey(line.stockItemId, line.isPromo)))
    .map(line => ({
        stockItemId: line.stockItemId,
        quantity: line.quantity,
        isPromo: isPromoLine(line.isPromo)
    }));

  const mergedItemDetails = [
    ...updatedExistingItems,
    ...newItems,
  ];

  const saleTotal = checkoutSale?.saleTotalAfterDiscount ?? 0;
  const saleTotalAsInt =
    checkoutSale?.saleTotalAfterDiscountAsInt ?? 0;

  return {
    customerId: user.id,
    stockItems: mergedItemDetails,
    customerTypeId: 2,
    totalPaid: saleTotal,
    totalPaidAsInt: saleTotalAsInt,
    amount: saleTotal,
    discountCode,
    address1: shipping?.address1 ?? user.address1,
    address2: shipping?.address2 ?? user.address2,
    address3: shipping?.address3 ?? user.address3,
    address4: shipping?.address4 ?? user.address4,
    postCode: shipping?.postCode ?? user.postCode,
    regionId: shipping?.regionId ?? user.regionId,
    regionName: shipping?.regionName ?? user.regionName,
    firstName: shipping?.firstName ?? user.firstName,
    lastName: shipping?.lastName ?? user.lastName,
    organization: shipping?.organization ?? user.organization,
    deliveryServiceId: settings?.deliveryServiceId,
  };
};


  useEffect(() => {
  const sale = getSaleData();
  if (!sale) return;

  dispatch(buildCheckoutSale(sale)).catch((err) => console.error(err));
}, [dispatch]);

useEffect(() => {
  if (!checkoutSale) return;

  setIsInitialLoading(true);
  dispatch({ type: ActionTypes.RESET_GET_PROMOTIONS_ITEMS });
  Promise.resolve(dispatch(getPromotionItems(checkoutSale, 1)))
    .catch((error) => {
      console.error("Failed to load promotion items:", error);
    })
    .finally(() => {
      setIsInitialLoading(false);
    });
}, [checkoutSale, dispatch]);

const handleSelect = (row) => {
  const payload = {
    allowHostGiftSystems: false,
    allowUpdate: true,
    discountCodesSettingId: null,
    promoStockItemId: row.PromotionStockItem?.Id || null,
    sale: {
      ...checkoutSale,
      address1: shipping?.address1 ?? user.address1,
      address2: shipping?.address2 ?? user.address2,
      address3: shipping?.address3 ?? user.address3,
      address4: shipping?.address4 ?? user.address4,
      postCode: shipping?.postCode ?? user.postCode,
      regionId: shipping?.regionId ?? user.regionId,
      regionName: shipping?.regionName ?? user.regionName,
      firstName: shipping?.firstName ?? user.firstName,
      lastName: shipping?.lastName ?? user.lastName,
    },  
    stockItemCode: row.StockItem?.StockCode || null,
    stockItemId: row.StockItem?.Id || null,
  };

setAddingItem(true);
const res = dispatch(getStockItemToAdd(payload));

res.then((res) => {
  const data = res?.value?.data;
  dispatch(storeCheckoutSale(data.sale));
  const promotionData = data.promotionData;
  const selectedProduct = {
    ...promotionData.product,                     
    title: promotionData.title,            
    originalPrice: promotionData.originalPrice,
    specialPrice: promotionData.specialPrice,
    stockItemCode: promotionData.stockItemCode,
    stockItemId: promotionData.stockItemId,
    quantity: promotionData.quantity
  };

  dispatch(addToCart(selectedProduct, selectedProduct.quantity, selectedProduct.originalPrice, selectedProduct.specialPrice, selectedProduct.stockItemId, selectedProduct.loyaltyPoints,true ));
  dispatch(getPromotionItems(data.sale, 1));
  dispatch(storeCheckoutSale(data.sale)); 
})
.catch((err) => {
  console.error("Error in getStockItemToAdd response:", err);
})
.finally(() => setAddingItem(false));
};

  // Price formatter
  const price = (v) => (v || v === 0 ? Number(v).toFixed(2) + "€" : "-");

 const columns = [
  // Hidden Id column
  {
    name: "#",
    selector: (row) => row.Id,
    omit: true,
  },

  // Hidden Promotion Name
  {
    name: t("Promotion"),
    selector: (row) => row.PromotionStockItem?.Promotion?.Name,
    omit: true,
  },

  // StockCode
  {
    name: t("StockCode"),
    cell: (row) => row.StockItem?.StockCode,
    width: "110px",
    sortable: true,
  },

  // Product (with icon template)
{
  name: t("Product"),
  cell: (row) => {
    const thumbnail = row.StockItem?.Thumbnail ||"/images/no-image.png";    

    return (
      <div style={{display: "flex", alignItems: "center",  gap: "8px", minWidth: 0,}}>
        <img src={thumbnail} alt={row.StockItem?.NameTranslation} style={{width: "32px",height: "32px",objectFit: "cover",borderRadius: "4px", flexShrink: 0, }}/>
        <span style={{whiteSpace: "nowrap",overflow: "hidden",textOverflow: "ellipsis",}}>
          {row.StockItem?.NameTranslation}
        </span>
      </div>
    );
  }, grow: 2,
},

  // Variation
  {
    name: t("Variation"),
    cell: (row) => {
      const v = row.StockItem?.VariationName;
      if (!v) return "";

      const shortened = v.length > 120 ? v.substring(0, 120) : v;

      return <span className="promotionvariationNames">{shortened}</span>;
    },
  },

  // Hidden StockItemId
  {
    name: t("StockItemId"),
    selector: (row) => row.StockItemId,
    omit: true,
  },

  // Hidden ProductId
  {
    name: t("ProductId"),
    selector: (row) => row.ProductId,
    omit: true,
  },

  // Product Group
  {
    name: t("Group"),
    cell: (row) =>
      row.StockItem?.ProductGroupName ? row.StockItem.ProductGroupName : "-",
  },

  // Price with CUSTOMER + OLD price + points
  {
    name: t("Price"),
    width: "160px",
    cell: (row) => {
      return (
        <div>
          {/* OLD PRICE */}
          {row.CustomerSpecialPrice ? (
            <span className="promo-original-price">
              {price(row.CustomerSpecialPrice)}
            </span>
          ) : null}

          {/* NEW PRICE */}
          <span className="newpromoPrice">
            {price(row.CustomerPrice)}
          </span>

          {/* POINTS */}
          {row.PromotionPriceInPoints ? (
            <div className="newpromoPrice">
              {price(row.PromotionPriceInPoints)}
            </div>
          ) : null}
        </div>
      );
    },
  },

  // PromoCode
  {
    name: t("PromoCode"),
    width: "180px",
    selector: (row) =>
      row.PromotionStockItem?.Promotion?.Code || row.PromotionCode,
  },

  // Hidden ORGroup (PromotionProductORingGroup)
  {
    name: t("PromotionProductORingGroup"),
    selector: (row) => row.PromotionStockItem?.ORGroup,
    omit: true,
  },

  // Hidden Promotion ORGroup
  {
    name: t("PromotionORingGroup"),
    selector: (row) => row.PromotionStockItem?.Promotion?.ORGroup,
    omit: true,
  },

  // MaxSaleQuantity (hidden)
  {
    name: t("MaxSaleQuantity"),
    selector: (row) => row.PromotionStockItem?.MaxSaleQuantity,
    omit: true,
  },

  // MaxCustomerQuantity (hidden)
  {
    name: t("MaxCustomerQuantity"),
    selector: (row) => row.PromotionStockItem?.MaxCustomerQuantity,
    omit: true,
  },
  {
  name: t("Actions"),
  cell: (row) => (
    <button
      onClick={() => handleSelect(row)}
      style={{
        padding: "6px 12px",
        background: "#43d9a3",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      {t("Select")}
    </button>
  ),
  width: "120px",
  ignoreRowClick: true,
  //allowOverflow: true,
  button: true,
}
];

  // Loader
  const loaderComponent = (
    <div
      style={{
        width: "100%",
        minHeight: "120px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "var(--theme-bg-surface)",
      }}
    >
      <div className="loader loader-vortex" />
    </div>
  );

  const isDataLoading = loading || addingItem || isInitialLoading;

  // Custom styles
  const customStyles = {
    table: {
      style: {
        backgroundColor: "var(--theme-bg-surface)",
        color: "var(--theme-text-primary)",
      },
    },
    headRow: {
      style: {
        minHeight: "44px",
        backgroundColor: "var(--theme-bg-elevated)",
        borderBottom: "2px solid var(--theme-border-soft)",
      },
    },
    headCells: {
      style: {
        padding: "8px 12px",
        fontSize: "14px",
        fontWeight: "600",
        color: "var(--theme-text-primary)",
      },
    },
    rows: {
      style: {
        minHeight: "40px",
        backgroundColor: "var(--theme-bg-surface)",
        color: "var(--theme-text-secondary)",
        borderBottomColor: "var(--theme-border-soft)",
      },
      stripedStyle: {
        backgroundColor: "var(--theme-bg-elevated)",
      },
      highlightOnHoverStyle: {
        backgroundColor: "var(--theme-bg-elevated)",
        color: "var(--theme-text-primary)",
        outline: "none",
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        color: "var(--theme-text-secondary)",
        padding: "8px 12px",
      },
    },
    noData: {
      style: {
        backgroundColor: "var(--theme-bg-surface)",
        color: "var(--theme-text-secondary)",
      },
    },
    progress: {
      style: {
        backgroundColor: "var(--theme-bg-surface)",
        color: "var(--theme-text-secondary)",
      },
    },
  };

  // Mobile view
const MobileView = ({ items }) => (
  <div className="mobile-accordion">
    {items.map((row, i) => {
      const thumbnail =
        row.StockItem?.Thumbnail || "/images/no-image.png";

      return (
        <details
          key={i}
          style={{
            border: "1px solid var(--theme-border-soft)",
            borderRadius: "8px",
            marginBottom: "12px",
            padding: "10px",
            background: "var(--theme-bg-surface)",
            color: "var(--theme-text-secondary)",
          }}
        >
          {/* HEADER */}
          <summary
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              gap: "10px",
              color: "var(--theme-text-primary)",
            }}
          >
            {/* Product */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <img
                src={thumbnail}
                alt=""
                style={{
                  width: "32px",
                  height: "32px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
              <span style={{ fontWeight: 700 }}>
                {row.StockItem?.NameTranslation}
              </span>
            </div>

            {/* Price */}
            <div style={{ textAlign: "right", fontSize: "13px" }}>
              {row.CustomerSpecialPrice && (
                <div style={{ textDecoration: "line-through" }}>
                  {price(row.CustomerSpecialPrice)}
                </div>
              )}
              <div className="newpromoPrice">
                {price(row.CustomerPrice)}
              </div>
            </div>
          </summary>

          {/* BODY */}
          <div style={{ marginTop: "12px", fontSize: "14px", color: "var(--theme-text-secondary)" }}>
            <p><b>{t("StockCode")}:</b> {row.StockItem?.StockCode || "-"}</p>

            <p>
              <b>{t("Variation")}:</b>{" "}
              {row.StockItem?.VariationName || "-"}
            </p>

            <p>
              <b>{t("Group")}:</b>{" "}
              {row.StockItem?.ProductGroupName || "-"}
            </p>

            <p>
              <b>{t("PromoCode")}:</b>{" "}
              {row.PromotionStockItem?.Promotion?.Code ||
                row.PromotionCode ||
                "-"}
            </p>

            {/* POINTS (if exists) */}
            {row.PromotionPriceInPoints && (
              <p>
                <b>{t("Points")}:</b>{" "}
                {price(row.PromotionPriceInPoints)}
              </p>
            )}

            {/* ACTION */}
            <button
              onClick={() => handleSelect(row)}
              disabled={addingItem}
              style={{
                marginTop: "12px",
                width: "100%",
                padding: "10px",
                background: "#43d9a3",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: addingItem ? "not-allowed" : "pointer",
                opacity: addingItem ? 0.6 : 1,
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              {addingItem ? t("Loading...") : t("Select")}
            </button>
          </div>
        </details>
      );
    })}
  </div>
);



  return (
    <div style={{ padding: "16px" }}>
      {/* Desktop View */}
      <Breakpoint medium up>
        <DataTable
          columns={columns}
          data={items}
          customStyles={customStyles}
          highlightOnHover
          striped
          responsive
          progressPending={isDataLoading}
          progressComponent={loaderComponent}
          noDataComponent={
            !isDataLoading ? (
              <div style={{ padding: "16px", color: "var(--theme-text-secondary)" }}>{t("There are no records to display")}</div>
            ) : null
          }
        />
      </Breakpoint>

      {/* Mobile View */}
      <Breakpoint small down>
        {isDataLoading ? (
          loaderComponent
        ) : items.length > 0 ? (
          <MobileView items={items} />
        ) : (
          <div style={{ padding: "16px", color: "var(--theme-text-secondary)" }}>{t("There are no records to display")}</div>
        )}
      </Breakpoint>
    </div>
  );

}

